/**
 * マスタ参照 API（/api/masters/*, /api/vessels/options）。
 * 担当: G1。他グループの画面からも利用する（共通選択部品・useCodeValues）。
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

// ---------------------------------------------------------------- 型
export type TaxRounding = '四捨五入' | '切り捨て' | '切り上げ';
export type SeaAreaClass = 'S' | 'A' | 'B';

export interface OperatorSetting {
  hasConsumptionTax: boolean;
  invoiceTaxSubsidy: boolean;
  taxRounding: TaxRounding;
  displayOrder: number;
  bulkApprovalFrom: string | null;
  bulkApprovalTo: string | null;
}

export interface BusinessOperatorDto {
  id: number;
  name: string;
  formalName: string | null;
  catchCsvFormat: string | null;
  isActive: boolean;
  note: string | null;
  setting?: OperatorSetting | null;
}

export interface CodeValues {
  fishingMethods: string[];
  /** 漁獲調査（漁場情報）の漁法一覧（表１。value＝名称） */
  catchFishingMethods: { value: string; label: string; code: number }[];
  foreignFishingTypes: string[];
  nationalities: string[];
  vesselActivities: string[];
  presence: string[];
  referenceSources: string[];
  bearerCounts: { value: number; label: string }[];
  catchSurveyStatuses: { value: string; label: string }[];
  fuelTypes: string[];
  taxRoundings: TaxRounding[];
  fuelTaxRoundings: string[];
  noticeRegistrationTypes: string[];
  noticeChangeTypes: string[];
  noticeApprovalStatuses: string[];
  vesselChangeReasons: string[];
  departureChangeReasons: string[];
  arrivalChangeReasons: string[];
  senkakuZoneNos: number[];
  equipmentCategories: string[];
  billingAddressees: string[];
  seaAreaClasses: SeaAreaClass[];
  rateTypes: string[];
  summarySubtitles: string[];
  summaryOutputPatterns: string[];
  summarySortOrders1: string[];
  summaryTaxSplits: string[];
  summaryTaxOrders: string[];
  quarters: string[];
  paymentStatuses: string[];
  scheduleStatuses: string[];
  organizationTypes: string[];
}

/** GET /api/vessels/options の要素（業務入力用の参加船選択肢） */
export interface VesselOptionDto {
  id: number;
  fiscalYear: number;
  vesselNo: number;
  displayOrder: number;
  businessOperatorId: number;
  businessOperatorName: string;
  name: string;
  nameKana: string;
  registrationNo: string;
  tonnage: number;
  phone: string | null;
  ownerName: string;
  fuelType: string | null;
  taxRounding: TaxRounding;
  isInvoiceRegistered: boolean;
  approvalFrom: string | null;
  approvalTo: string | null;
  captains: { name: string }[];
  crew: { name: string }[];
  capacity: { boardingCount: number; totalBoardingCount: number } | null;
  fishingMethods: string[];
}

export interface PortDto {
  id?: number;
  businessOperatorId: number;
  name: string;
  displayOrder: number;
}

export interface RateSetDto {
  consumptionTaxRate: number;
  invoiceDeductionRate: number;
  lightOilTaxRate: number;
  lubricantCoefficient: number;
  invoiceStartDate: string;
}

// ---------------------------------------------------------------- クエリキー
export const masterKeys = {
  all: ['masters'] as const,
  codeValues: () => [...masterKeys.all, 'code-values'] as const,
  operators: (year?: number, includeInactive?: boolean) => [...masterKeys.all, 'business-operators', year ?? null, !!includeInactive] as const,
  ports: (year: number, operatorId?: number | null) => [...masterKeys.all, 'ports', year, operatorId ?? null] as const,
  vesselOptions: (p: VesselOptionsParams) => [...masterKeys.all, 'vessel-options', p] as const,
  rateSet: (date: string) => [...masterKeys.all, 'rate-set', date] as const,
  year: (name: string, year: number) => [...masterKeys.all, name, year] as const,
};

// ---------------------------------------------------------------- フック
/** コード値（GET /api/masters/code-values）。ほぼ不変のため長めにキャッシュ */
export function useCodeValues() {
  return useQuery({
    queryKey: masterKeys.codeValues(),
    queryFn: ({ signal }) => api.get<CodeValues>('/masters/code-values', undefined, { signal }),
    staleTime: 60 * 60_000,
  });
}

/** 事業実施者一覧（漁協ユーザーは自組織のみが返る） */
export function useBusinessOperators(fiscalYear?: number, opts: { includeInactive?: boolean; enabled?: boolean } = {}) {
  return useQuery({
    queryKey: masterKeys.operators(fiscalYear, opts.includeInactive),
    queryFn: ({ signal }) =>
      api.get<BusinessOperatorDto[]>(
        '/masters/business-operators',
        { fiscalYear, includeInactive: opts.includeInactive ? 'true' : undefined },
        { signal },
      ),
    staleTime: 5 * 60_000,
    enabled: opts.enabled ?? true,
  });
}

export interface VesselOptionsParams {
  fiscalYear: number;
  businessOperatorId?: number | null;
  date?: string;
  dateFrom?: string;
  dateTo?: string;
}

/** 参加船の選択肢（承認済み・期間有効のみ） */
export function useVesselOptions(p: VesselOptionsParams, enabled = true) {
  return useQuery({
    queryKey: masterKeys.vesselOptions(p),
    queryFn: ({ signal }) =>
      api.get<VesselOptionDto[]>(
        '/vessels/options',
        { fiscalYear: p.fiscalYear, businessOperatorId: p.businessOperatorId ?? undefined, date: p.date, dateFrom: p.dateFrom, dateTo: p.dateTo },
        { signal },
      ),
    enabled,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

/** 港名（事業実施者別） */
export function usePorts(fiscalYear: number, businessOperatorId?: number | null, enabled = true) {
  return useQuery({
    queryKey: masterKeys.ports(fiscalYear, businessOperatorId),
    queryFn: ({ signal }) =>
      api.get<{ items: PortDto[] }>(`/masters/years/${fiscalYear}/ports`, { businessOperatorId: businessOperatorId ?? undefined }, { signal }),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** 判定日の税率一式（L-01） */
export function useRateSet(date: string | null | undefined) {
  return useQuery({
    queryKey: masterKeys.rateSet(date ?? ''),
    queryFn: ({ signal }) => api.get<RateSetDto>('/masters/rate-periods/lookup', { date: date ?? undefined }, { signal }),
    enabled: !!date,
    staleTime: 10 * 60_000,
  });
}

/** マスタ更新（PUT）共通。保存後にマスタ系クエリを無効化する */
export function useSaveMaster<TBody>(path: string, method: 'put' | 'post' = 'put') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TBody) => (method === 'put' ? api.put<unknown>(path, body) : api.post<unknown>(path, body)),
    onSuccess: () => qc.invalidateQueries({ queryKey: masterKeys.all }),
  });
}
